import type { ReactNode } from 'react';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import type { EFeatureFlag } from '@/lib/feature-flags/keys';
import {
  FeatureCheckFailedPlaceholder,
  FeatureCheckLoadingPlaceholder,
} from './FeatureCheckFailedPlaceholder';

interface FeatureFlagProps {
  flag: EFeatureFlag;
  /** What to show when the tenant genuinely does not have this module. */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Gates children on a feature flag.
 *
 * Every flag reads `false` until the server's flag map arrives, so "off" and
 * "not asked yet" are the same value. Callers that show something in place of
 * the feature — a page-level placeholder — get told which of the two it is:
 * a spinner while the answer is in flight, and a retry panel if the request
 * failed. Only a flag the server actually reported off gets `fallback`.
 *
 * A caller that passes no fallback wants the feature to disappear quietly —
 * a toolbar button, say — and still gets nothing, because a connection error
 * panel where a button used to be would be worse than the button's absence.
 */
export function FeatureFlag({ flag, fallback, children }: FeatureFlagProps) {
  const { enabled, isResolved, isError, refetch } = useFeatureFlag(flag);

  if (enabled) return <>{children}</>;
  if (fallback === undefined) return null;

  if (!isResolved) {
    return isError ? (
      <FeatureCheckFailedPlaceholder onRetry={refetch} />
    ) : (
      <FeatureCheckLoadingPlaceholder />
    );
  }

  return <>{fallback}</>;
}
